import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { useAuth } from "@/hooks/use-auth";
import { useCustomerView } from "@/hooks/use-customer-view";
import { getDataSource } from "@/lib/data-source";
import { StatusChip } from "@/components/StatusChip";
import { format } from "date-fns";
import { Plus, ArrowRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/app/customer/jobs/")({ component: CustomerJobsIndex });

function CustomerJobsIndex() {
  const { demoMode } = useDemoMode();
  const { profile } = useAuth();
  const { active: viewingAsCustomer } = useCustomerView();
  const isStaff = profile?.role === "staff" || profile?.role === "admin";
  const ds = useMemo(
    () => getDataSource(demoMode && (isStaff || viewingAsCustomer)),
    [demoMode, isStaff, viewingAsCustomer],
  );

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ["customer-jobs", demoMode, profile?.customer_id],
    queryFn: () =>
      ds.listJobs(profile?.customer_id ? { customer_id: profile.customer_id } : undefined),
  });

  const filtered = jobs.filter((j) => {
    const matchesSearch =
      !search ||
      j.job_title.toLowerCase().includes(search.toLowerCase()) ||
      j.id.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || j.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const active = filtered.filter((j) => j.status !== "Completed");
  const completed = filtered.filter((j) => j.status === "Completed");
  const allStatuses = [...new Set(jobs.map((j) => j.status))].sort();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">My jobs</h1>
          <p className="text-muted-foreground">Track all your print jobs in one place.</p>
        </div>
        <Link to="/app/customer/new-job" search={{ product: undefined }}>
          <Button className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90 gap-1.5">
            <Plus className="h-4 w-4" /> New job
          </Button>
        </Link>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search jobs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-full pl-9 w-56"
          />
        </div>
        <select
          className="rounded-full border border-border bg-background px-3 py-1.5 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All statuses</option>
          {allStatuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-2xl bg-muted animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl border border-border bg-card p-12 text-center shadow-soft">
          <p className="text-muted-foreground">No jobs found.</p>
          <Link
            to="/app/customer/new-job"
            search={{ product: undefined }}
            className="mt-4 inline-block"
          >
            <Button className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90 gap-1.5">
              <Plus className="h-4 w-4" /> Submit your first job
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {active.length > 0 && (
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Active ({active.length})
              </h2>
              <div className="space-y-2">
                {active.map((job) => (
                  <JobRow key={job.id} job={job} />
                ))}
              </div>
            </section>
          )}
          {completed.length > 0 && (
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Completed ({completed.length})
              </h2>
              <div className="space-y-2">
                {completed.map((job) => (
                  <JobRow key={job.id} job={job} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function JobRow({ job }: { job: any }) {
  const dueDate = job.due_date ? new Date(job.due_date) : null;
  return (
    <Link to="/app/customer/jobs/$id" params={{ id: job.id }}>
      <div className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-card p-4 transition-colors hover:bg-muted/50">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-muted-foreground">#{job.id.slice(0, 8)}</span>
            <StatusChip status={job.status} />
          </div>
          <div className="mt-1 font-medium truncate">{job.job_title}</div>
          {job.gang_sheet_name && job.gang_sheet_name !== job.job_title && (
            <div className="text-xs text-muted-foreground truncate">
              Design: {job.gang_sheet_name}
            </div>
          )}
          <div className="text-sm text-muted-foreground">
            {job.product_type}
            {job.quantity ? ` · qty ${job.quantity}` : ""}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {dueDate && (
            <span className="text-xs text-muted-foreground">Due {format(dueDate, "d MMM")}</span>
          )}
          <span className="text-xs text-muted-foreground">
            {format(new Date(job.created_at), "d MMM yyyy")}
          </span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </Link>
  );
}
